<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import ToggleSwitch from 'primevue/toggleswitch';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { Virtualizer } from 'virtua/vue';
import {
  ButtonType,
  CustomButton,
  FormField,
  FormFieldReorderDialog,
  FormReminderDialog,
  FormDiscardChangesAlert
} from '@/components';
import { useStudyStore } from '@/stores/study';
import { FormFieldType, type FormField as Field } from '@/models';
import { useFormBuilder } from '@/stores/formBuilder';
import { cloneDeep } from 'lodash';

const studyStore = useStudyStore();
const builder = useFormBuilder();
const headerRef = ref();
const virtualizerRef = ref();

const headerHeight = computed(
  () => ((headerRef.value as HTMLElement | null)?.clientHeight || 400) + 50
);

const expandAll = () => {
  const currentlyExpanded = fieldsExpanded.value;
  builder.form!.items?.forEach(
    field => (field.expanded = !currentlyExpanded)
  );
};

const fieldsExpanded = computed(() =>
  builder.form!.items?.every(field => field.expanded)
);

const publicLink = computed(
  () =>
    `${import.meta.env.VITE_APP_FRONTEND_BASE_URL}/form/${
      builder.form!.identifier
    }`
);

const handleAddField = async (field: Field) => {
  builder.addFormField(field);
};

const handleDeleteField = (field: Field) => {
  builder.deleteFormField(field);
};

const handleCloneField = (field: Field) => {
  builder.cloneFormField(field);
};

const openFieldReorderDialog = ref(false);
const handleReorderFields = () => {
  openFieldReorderDialog.value = !openFieldReorderDialog.value;
};

const openFormReminderDialog = ref(false);
const handleRemindersDialog = () => {
  openFormReminderDialog.value = !openFormReminderDialog.value;
};

const deleteRemindersConfig = () => {
  delete builder.form!.reminders;
};

const discardFormChangesAlert = () => {
  builder.showOnlyDiscardViews = !builder.showOnlyDiscardViews;
  builder.showDiscardChangesDialog();
};

const openPublicForm = () => {
  window.open(`/form/${builder.form!.identifier}`, '_blank');
};

const copyPublicFormLink = () => {
  navigator.clipboard.writeText(publicLink.value);
};

const checkErrors = () => {
  const items = cloneDeep(builder.form!.items) || [];
  items.forEach(item => {
    if (!item.label || item.label.trim() === '') {
      item.error = `Label is required`;
    } else {
      delete item.error;
    }

    if (item.type === FormFieldType.CHOICE) {
      const choiceErrors = item.options.choices?.filter(
        choice =>
          !choice.label ||
          choice.label.trim() === '' ||
          choice.value === undefined ||
          choice.value === null ||
          choice.value === ''
      );
      if (choiceErrors && choiceErrors.length > 0) {
        item.error = `Choices must have a label and value`;
      }
    }

    if (item.variableName) {
      items
        .filter(
          i =>
            i.identifier !== item.identifier &&
            i.variableName?.toLowerCase() === item.variableName?.toLowerCase()
        )
        .forEach(duplicate => {
          duplicate.error = `Variable name must be unique`;
          item.error = `Variable name must be unique`;
        });
    }

    //TODO: Add more validation for other field types as needed
  });

  if (items.filter(item => item.error).length > 0) {
    builder.form!.items = items;

    const index = items?.findIndex(item => item.error);
    if (index !== undefined && index > -1) {
      nextTick(() => {
        virtualizerRef.value.scrollToIndex(index);
      });
    }
    return;
  }

  builder.patchForm();
};

const openPrintForm = () => {
  window.open(`/form/${builder.form!.identifier}/print`, '_blank');
};

const scrollToTheField = (field: Field) => {
  nextTick(() => {
    const index = builder.form!.items?.findIndex(
      item => item.identifier === field.identifier
    );
    if (index != undefined && index > -1) {
      virtualizerRef.value.scrollToIndex(index);
    }
  });
};
</script>

<template>
  <div class="form-builder-root">
    <div class="header-root">
      <div class="header">
        <div class="header-content">
          <div class="header-title">
            <InputText type="text"
              class="input-title"
              v-model="builder.form!.title"
              placeholder="Form name"/>
          </div>
          <div class="header-buttons">
            <CustomButton :text="fieldsExpanded ? 'Collapse all' : 'Expand all'"
              :type="ButtonType.OUTLINED"
              @onClick="expandAll"
              :icon="
                fieldsExpanded
                  ? { icon: 'caret-down', style: 'fas' }
                  : { icon: 'caret-right', style: 'fas' }
              "/>
            <CustomButton text="Discard"
              :margin-left="16"
              :disabled="builder.isFormPristine"
              @onClick="discardFormChangesAlert"
              :type="ButtonType.OUTLINED"/>
            <CustomButton text="Save changes"
              :margin-left="16"
              :disabled="builder.isFormPristine"
              @onClick="checkErrors"
              :type="ButtonType.POSITIVE"/>
          </div>
        </div>
      </div>
    </div>
    <div class="form-builder">
      <div class="settings-section" ref="headerRef">
        <Card class="settings-card">
          <template #content>
            <div class="settings-root">
              <h3 class="settings-label">Settings</h3>
              <div class="open-form">
                <div>Share to web</div>
                <ToggleSwitch v-model="builder.form!.anonymousResponsesAllowed"/>
                <CustomButton text="Open as public form"
                  :disabled="builder.form!.anonymousResponsesAllowed != true"
                  @onClick="openPublicForm"
                  :type="ButtonType.OUTLINED"/>
              </div>
              <div class="form-link copy-link"
                v-if="builder.form!.anonymousResponsesAllowed == true">
                <InputGroup>
                  <InputText type="text" disabled="true" :value="publicLink" />
                  <InputGroupAddon>
                    <button v-ripple @click="copyPublicFormLink">
                      Copy link
                    </button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
              <div class="link-description"
                v-if="builder.form!.anonymousResponsesAllowed == true">
                People with the the link can see the form without needing an
                account.
              </div>

              <div class="scheduled-reminders"
                v-if="!studyStore.selectedStudy?.longitudinal">
                <div class="scheduled-reminders-label">Scheduled reminders</div>
                <div class="reminder-buttons">
                  <CustomButton text="Create"
                    :type="ButtonType.OUTLINED_PRIMARY"
                    v-if="!builder.form!.reminders"
                    @onClick="handleRemindersDialog"/>
                  <CustomButton text="Edit"
                    :type="ButtonType.OUTLINED_PRIMARY"
                    v-if="builder.form!.reminders"
                    @onClick="handleRemindersDialog"/>
                  <CustomButton text="Delete"
                    :margin-left="16"
                    @onClick="deleteRemindersConfig"
                    :type="ButtonType.NEGATIVE_OUTLINED"
                    v-if="builder.form!.reminders"/>
                </div>
              </div>

              <!-- Hiding this until form export & import is implemented. v-if="studyStore.selectedStudy?.flags?.importExportStudy" -->
              <div class="import-export" v-if="false">
                <div class="export">
                  <div class="export-label">Export form</div>
                  <CustomButton text="Export"
                    :icon="{ style: 'far', icon: 'file-export' }"
                    :type="ButtonType.OUTLINED"/>
                </div>
                <div class="import">
                  <div class="import-label">Import forms</div>
                  <CustomButton text="Import"
                    :icon="{ style: 'far', icon: 'file-import' }"
                    :type="ButtonType.OUTLINED"/>
                </div>
              </div>

              <div class="print">
                <CustomButton text="Print form"
                  :icon="{ style: 'fas', icon: 'print' }"
                  :type="ButtonType.OUTLINED"
                  @onClick="openPrintForm"/>
              </div>
            </div>
          </template>
        </Card>
        <h4 class="form-fields-title">Form fields</h4>
      </div>
      <Virtualizer :data="builder.form!.items!"
        :start-margin="headerHeight"
        ref="virtualizerRef">
        <template #default="data">
          <div class="form-field-root"
            :key="
              JSON.stringify({
                identifier: data.item.identifier,
                error: data.item.error,
              })
            ">
            <FormField :field="data.item"
              @onAddField="handleAddField"
              @onDeleteField="handleDeleteField"
              @onCloneField="handleCloneField"
              @onReorderFields="handleReorderFields"
              @onFieldTypeChange="builder.handleFieldTypeChange"
              @onChildFieldClick="scrollToTheField"/>
          </div>
        </template>
      </Virtualizer>
    </div>
  </div>

  <FormFieldReorderDialog :visible="openFieldReorderDialog"
    v-if="openFieldReorderDialog"
    @onClose="handleReorderFields"/>

  <FormReminderDialog :visible="openFormReminderDialog"
    v-if="openFormReminderDialog"
    @onClose="handleRemindersDialog"/>

  <FormDiscardChangesAlert :visible="builder.openDiscardChangesAlert"
    v-if="builder.openDiscardChangesAlert"
    :discard="builder.showOnlyDiscardViews"
    @onSave="builder.patchForm"
    @onClose="builder.cancelDiscardChangesDialog"
    @onDiscard="builder.discardFormChanges"/>
</template>

<style scoped lang="scss">
@import "../../../assets/main.scss";
.form-builder-root {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.form-builder {
  padding: 40px 80px;
  flex: 1;
  overflow: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.header-root {
  position: sticky;
  z-index: 1;
  background-color: white;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
.header {
  padding: 16px 80px;
}
.header-content {
  max-width: 1000px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-title {
  flex: 1;
  padding-right: 16px;

  .p-inputtext {
    width: 100%;
    font-weight: 700;
    font-size: 24px;
    border: 1px solid transparent;
    box-shadow: none;

    &:hover {
      border: 1px solid #cbd5e1;
      box-shadow: 0px 0px #0000, 0px 0px #0000,
        0px 1px 2px 0px rgba(18, 18, 23, 0.05);
    }
  }
}
.header-buttons {
  display: flex;
}
.settings-root {
  max-width: 400px;
}
.settings-label {
  font-weight: 700;
}
.open-form {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.form-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1em;
}
.link-description {
  font-size: 14px;
  font-weight: 400;
}
.copy-link {
  :deep(.p-inputtext) {
    font-size: 14px;
    cursor: text;
  }
  :deep(.p-inputgroupaddon) {
    padding: 0px;

    button {
      padding: 0.5rem 0.75rem;
      border: none;
      background: none;
      cursor: pointer;
    }

    &:hover {
      cursor: pointer;
      background-color: hsla(0, 0%, 0%, 0.15);
    }
  }
}
.import-export {
  max-width: 250px;
  display: flex;
  justify-content: space-between;
  margin-top: 32px;

  .export-label,
  .import-label {
    font-weight: 700;
    font-size: 16px;
    padding-bottom: 8px;
  }
}
.form-fields {
  height: 100vh;
  max-width: 1000px;
}
.settings-section {
  max-width: 1000px;
  margin-bottom: 40px;

  :deep(.p-card) {
    border-radius: 4px;
    box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
      0 1px 3px 1px rgba(15, 15, 15, 0.05);
    padding: 40px;

    .p-card-body {
      padding: 0px;
    }
  }
}
.settings-card {
  margin-bottom: 60px;

  .scheduled-reminders {
    margin-top: 32px;

    .scheduled-reminders-label {
      font-weight: 700;
      font-size: 16px;
      padding-bottom: 8px;
    }

    .reminder-buttons {
      display: flex;
    }
  }
}
.form-fields-title {
  font-weight: 700;
  font-size: 16px;
}
.form-field-root {
  padding-bottom: 32px;
}
</style>
